import { get, isArray, isDate, isObject } from 'lodash'
import keyBy from 'lodash/keyBy'
import mapValues from 'lodash/mapValues'

import { datter } from '@/lib/utils'
import {
  BasketProductOption as BasketProductOptionType,
  ProductOption as ProductOptionType,
  ProductOptionValidator as ProductOptionValidatorType
} from '@/types'

export function clean(
  values: any,
  productOptions?: ProductOptionType[]
): BasketProductOptionType[] {
  const newValues = Object.keys(values).map(key => {
    const value = values[key]

    if (isArray(value)) {
      const newValue = value
        .filter((item: any) => Boolean(item))
        .map((item: any) => [item, '1'].join('='))
      return { code: key, value: newValue.join(',') }
    }

    if (isObject(value) && !isDate(value)) {
      const newValue = Object.keys(value)
        .filter((itemKey: string) => get(value, itemKey))
        .map((itemKey: string) => [
          itemKey,
          get(value, itemKey) === true ? 1 : get(value, itemKey)
        ].join('='))

      return { code: key, value: newValue.join(',') }
    }

    if (value === true) {
      return { code: key, value: 'true' }
    }

    if (typeof value === 'string') {
      const isCpf =
        productOptions
          ?.find(option => option.code === key)
          ?.validators?.some(validator => validator.type === 'cpf') ||
        /^\d{3}\.\d{3}\.\d{3}-\d{2}$/.test(value)
      if (isCpf) {
        return { code: key, value: value.replace(/\D/g, '') }
      }
    }

    return { code: key, value }
  })

  return newValues.filter(({ value }) => value !== undefined && value !== null && value !== '')
}

export function getValueByType(type: string, value: any) {
  switch (type) {
    case 'integer':
      return value !== undefined && value !== null && value !== '' ? Number(value) : ''
    case 'text':
      return value || ''
    case 'boolean':
      return value === 'true' || value === true
    case 'date':
      return value ? datter(value).output : ''
    case 'multioption':
      return Array.isArray(value)
        ? value
        : value?.split?.(',').filter((item: any) => Boolean(item) && typeof item === 'string') || []
    case 'option':
      return value || ''
    default: {
      return value
    }
  }
}

export function initialValues(options?: ProductOptionType[]) {
  if (options) {
    const newOptions = options
      .map(option => {
        const { code, type, defaultValue, isRequired, validators } = option

        if (defaultValue !== undefined && defaultValue !== null && defaultValue !== '') {
          return { code, value: getValueByType(type, defaultValue) }
        }

        if (type === 'integer' && isRequired) {
          const minValidator = validators?.find(validator => validator.type === 'min')?.value
          const initialNum =
            minValidator !== undefined && minValidator !== '' ? Number(minValidator) : 0
          return { code, value: initialNum }
        }

        return null
      })
      .filter((optionItem): optionItem is { code: string; value: any } => optionItem !== null)

    return mapValues(keyBy(newOptions, 'code'), 'value')
  }
  return {}
}

export function validate(options: ProductOptionType[], values: any) {
  let validation = false

  const newOptions = keyBy(
    options.filter(option => option.isRequired),
    'code'
  )

  Object.keys(newOptions).forEach(key => {
    const value = values[key]
    if (
      value === undefined ||
      value === null ||
      value === '' ||
      (Array.isArray(value) && value.length === 0)
    ) {
      validation = true
    }
  })

  return validation
}

export function propsValidators(validators: ProductOptionValidatorType[] = []) {
  const inputProps = mapValues(keyBy(validators, 'type'), 'value')

  const { min, max, maxlength, minlength } = inputProps

  const helperText = () => {
    if (min && max) return `Mínimo: ${min}, Máximo: ${max}`
    if (min) return `Mínimo: ${min}`
    if (max) return `Máximo: ${max}`
    if (minlength && maxlength) return `Entre ${minlength} e ${maxlength} caracteres`
    if (minlength) return `Mínimo de ${minlength} caracteres`
    if (maxlength) return `Máximo de ${maxlength} caracteres`

    return null
  }

  return {
    min: min !== undefined && min !== '' ? Number(min) : undefined,
    max: max !== undefined && max !== '' ? Number(max) : undefined,
    maxLength: Number(maxlength) ? Number(maxlength) : undefined,
    minLength: Number(minlength) ? Number(minlength) : undefined,
    description: helperText()
  }
}
